import axios from 'axios';

export interface TenantDto {
  tenantId: number;
  vendorId: number;
  vendorTenantId: number;
  tenantName: string;
  tenantDescription?: string | null;
  enabled: boolean;
  endpointUrl?: string | null;
  acornTenantSlug?: string | null;
  clientId: string;
  createdAt: string;
  updatedAt: string;
}

export interface TenantCreateDto {
  vendorId: number;
  vendorTenantId: number;
  tenantName: string;
  tenantDescription?: string | null;
  enabled?: boolean;
  endpointUrl?: string | null;
  acornTenantSlug?: string | null;
  clientId: string;
  clientSecret: string;
}

export interface TenantUpdateDto {
  tenantName?: string;
  tenantDescription?: string | null;
  enabled?: boolean;
  endpointUrl?: string | null;
  acornTenantSlug?: string | null;
  clientId?: string;
  clientSecret?: string;
}

const base = '/api/Tenants';
// Dev-mode logic:
// Enabled if:
// 1. Explicit flag VITE_TENANTS_DEV_MODE=true
// 2. Running in Vite dev (import.meta.env.DEV) and flag is not explicitly 'false'
// Use VITE_TENANTS_DEV_MODE=false to force-disable.
const rawFlag = String((import.meta as any)?.env?.VITE_TENANTS_DEV_MODE ?? '').toLowerCase();
const devMode = rawFlag === 'true' || (import.meta.env.DEV && rawFlag !== 'false');

// Simple local storage mock store for dev-mode
const LS_KEY = 'acorn:tenants:mock';
function readMock(): TenantDto[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}
function writeMock(items: TenantDto[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
  } catch {}
}
function nextId(items: TenantDto[]): number {
  return (items.reduce((m, x) => Math.max(m, x.tenantId), 0) || 0) + 1;
}

// Seed example data (only if empty) based on provided sample
function ensureSeed() {
  const items = readMock();
  if (items.length > 0) return;
  const now = new Date().toISOString();
  const seed: TenantDto[] = [
    {
      tenantId: 1,
      vendorId: 1,
      vendorTenantId: 2284,
      tenantName: 'Dev: ClassLink Test District',
      tenantDescription: 'ClassLink dev test tenant',
      enabled: true,
      endpointUrl: 'https://classlinkcertification3-vn-v2.rosterserver.com/ims/oneroster/v1p1',
      acornTenantSlug: '',
      clientId: 'ef40a97c4fc4ee95fb9ccb52',
      createdAt: now,
      updatedAt: now,
    },
    {
      tenantId: 2,
      vendorId: 1,
      vendorTenantId: 1356,
      tenantName: 'ClassLink Certification',
      tenantDescription: 'ClassLink certification tenant',
      enabled: true,
      endpointUrl: 'https://certs-nj-v2.rosterserver.com/ims/oneroster/v1p1',
      acornTenantSlug: '',
      clientId: 'eaef617fb457fe7c23265518',
      createdAt: now,
      updatedAt: now,
    }
  ];
  writeMock(seed);
}

export const tenantService = {
  async list(): Promise<TenantDto[]> {
    if (devMode) {
      ensureSeed();
      return readMock();
    }
    try {
      const { data } = await axios.get<TenantDto[]>(base);
      return data;
    } catch (e: any) {
      if (e?.response?.status === 403 && devMode) {
        ensureSeed();
        return readMock();
      }
      throw e;
    }
  },
  async get(id: number): Promise<TenantDto> {
    if (devMode) {
      const found = readMock().find(x => x.tenantId === id);
      if (!found) throw new Error('Not found');
      return found;
    }
    const { data } = await axios.get<TenantDto>(`${base}/${id}`);
    return data;
  },
  async create(dto: TenantCreateDto): Promise<TenantDto> {
    if (devMode) {
      const items = readMock();
      const now = new Date().toISOString();
      const item: TenantDto = {
        tenantId: nextId(items),
        vendorId: dto.vendorId,
        vendorTenantId: dto.vendorTenantId,
        tenantName: dto.tenantName,
        tenantDescription: dto.tenantDescription ?? '',
        enabled: dto.enabled ?? true,
        endpointUrl: dto.endpointUrl ?? '',
        acornTenantSlug: dto.acornTenantSlug ?? '',
        clientId: dto.clientId,
        createdAt: now,
        updatedAt: now,
      };
      items.push(item);
      writeMock(items);
      return item;
    }
    const { data } = await axios.post<TenantDto>(base, dto);
    return data;
  },
  async update(id: number, dto: TenantUpdateDto): Promise<TenantDto> {
    if (devMode) {
      const items = readMock();
      const idx = items.findIndex(x => x.tenantId === id);
      if (idx < 0) throw new Error('Not found');
      const updated: TenantDto = {
        ...items[idx],
        tenantName: dto.tenantName ?? items[idx].tenantName,
        tenantDescription: dto.tenantDescription ?? items[idx].tenantDescription,
        enabled: dto.enabled ?? items[idx].enabled,
        endpointUrl: dto.endpointUrl ?? items[idx].endpointUrl,
        acornTenantSlug: dto.acornTenantSlug ?? items[idx].acornTenantSlug,
        clientId: dto.clientId ?? items[idx].clientId,
        updatedAt: new Date().toISOString(),
      };
      items[idx] = updated;
      writeMock(items);
      return updated;
    }
    const { data } = await axios.put<TenantDto>(`${base}/${id}`, dto);
    return data;
  },
  async delete(id: number): Promise<void> {
    if (devMode) {
      const items = readMock().filter(x => x.tenantId !== id);
      writeMock(items);
      return;
    }
    await axios.delete(`${base}/${id}`);
  },
  async enable(id: number): Promise<void> {
    if (devMode) {
      const items = readMock();
      const idx = items.findIndex(x => x.tenantId === id);
      if (idx >= 0) {
        items[idx].enabled = true;
        items[idx].updatedAt = new Date().toISOString();
        writeMock(items);
      }
      return;
    }
    await axios.patch(`${base}/${id}/enable`);
  },
  async disable(id: number): Promise<void> {
    if (devMode) {
      const items = readMock();
      const idx = items.findIndex(x => x.tenantId === id);
      if (idx >= 0) {
        items[idx].enabled = false;
        items[idx].updatedAt = new Date().toISOString();
        writeMock(items);
      }
      return;
    }
    await axios.patch(`${base}/${id}/disable`);
  },
};
